<script setup>
import { ref, computed, defineAsyncComponent, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useApplicationStore } from '@stores/application';
import { useAuthRunningTaskStore } from '@stores/auth/runningTask';
import { useEmoticonStore } from '@stores/emoticon';
/***/
import { useAuthUser } from '@compositions/Auth';
/**/
import UserModel from '@models/UserModel';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const Wrapper = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Me/Task/Running/Process/Wrapper'));

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Stores
const applicationStore = useApplicationStore();
const runningTaskStore = useAuthRunningTaskStore();
const emoticonStore = useEmoticonStore();

// Application courante
const currentApplication = computed(() => applicationStore.getCurrent);

// Tâche à gérer
const task = computed(() => {
    return runningTaskStore.getRunningTaskByPublicId(route.params.taskPublicId);
});

// Redirige l'agent vers la liste de ses tâches lorsque la tâche passe en statut de retour
watch(task, (newTask) => {
    if(task.value.status === 'RETURN_WAITING') {
        runningTaskStore.resetIncomingTaskList();
        router.push({
            name: 'me.running-tasks.index',
            query: {
                'from-processing-task': task.value.publicId
            }
        });
    }
});

const { authUser, authUserHasPermission } = useAuthUser({});
const allowed = ref(false);

// Chargement de la tâche
const loadingTask = ref(false);
const loadTaskError = ref(false);
const loadTask = (() => {

    if(loadingTask.value) {
        return Promise.reject();
    }

    loadingTask.value = true;
    loadTaskError.value = false;

    return runningTaskStore.loadRunningTaskByPublicId(route.params.taskPublicId).then(() => {
        if(task.value.canProcess) {
            return Promise.resolve();
        }
        return Promise.reject();
    }).finally(() => {
        loadingTask.value = false;
    });

});

// Chargement des émoticônes
const loadingEmoticons = ref(false);
const loadEmoticons = (() => {
    if(loadingEmoticons.value) {
        return Promise.reject();
    }

    loadingEmoticons.value = true;

    return emoticonStore.loadCategories().then(() => {
        return Promise.resolve();
    }).catch(() => {
        return Promise.resolve(); // Les émoticônes sont secondaires
    }).finally(() => {
        loadingEmoticons.value = false;
    });
});

// Retourne si l'utilisateur est un agent
const isAgent = computed(() => {
    if(! authUser.value) {
        return false;
    }
    return (new UserModel(authUser.value)).isAgent();
});

onMounted(() => {

    const canConsult = (authUserHasPermission('consult tasks') && isAgent.value);
    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        loadTask().then(() => {
            loadEmoticons().then(() => {
                document.title = task.value.title + ' - ' + currentApplication.value.name;
            });
        }).catch(() => {
            loadTaskError.value = true;
        }).finally(() => {
            allowed.value = true;
        });
    }
});
</script>

<template>
    <layout
        v-if="allowed"
        wrapper-content-classes="wrapper_edit-add wrapper_task-process"
    >
        <loader v-if="loadingTask || loadingEmoticons"></loader>
        <p
            v-else-if="loadTaskError || ! task"
            class="generic-message generic-message-error"
        >
            {{ $t('task.running.processing.task.loading.error') }}
        </p>
        <wrapper
            v-else-if="task"
            :task="task"
        ></wrapper>
    </layout>
</template>
